import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppStore } from "app/redux-store-interactions";
import { featuresConfig, WidgetKey } from "config/features";
import { alignmentActions } from "features/alignment/slice";
import {
    explorerActions,
    selectCanAddWidget,
    selectMaximized,
    selectMaximizedHorizontal,
    selectWidgetLayout,
    selectWidgets,
    selectWidgetSlot,
} from "slices/explorer";
import { getTakenWidgetSlotCount } from "slices/explorer/utils";
import { mixpanel } from "utils/mixpanel";

export function useOpenWidget() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const store = useAppStore();

    return useCallback(
        (widgetKey: WidgetKey, params: { replace?: WidgetKey } | { force?: boolean } = {}) => {
            const state = store.getState();
            const canAddWidget = selectCanAddWidget(state);
            const widgetSlot = selectWidgetSlot(state);
            const widgetLayout = selectWidgetLayout(state);
            const widgets = selectWidgets(state);
            const maximized = selectMaximized(state);
            const maximizedHorizontal = selectMaximizedHorizontal(state);

            // Some items that appear in widget list are not really widgets and have different opening logic
            if (widgetKey === featuresConfig.globalSearch.key) {
                trackOpened(widgetKey);
                dispatch(explorerActions.setGlobalSearchOpen(true));
                return;
            }

            // Follow path is no more, so show snackbar and redirect to Horizontal alignment
            let isFollowPath = false;
            if (widgetKey === featuresConfig.followPath.key) {
                widgetKey = featuresConfig.horizontalAlignment.key;
                isFollowPath = true;
            }

            function showFollowPathSnackbar() {
                if (!isFollowPath) {
                    return;
                }

                dispatch(alignmentActions.setShowFollowPathRedirectionSnackbar(true));
            }

            if (widgets.includes(widgetKey)) {
                showFollowPathSnackbar();
                return;
            }

            const replace = "replace" in params ? params.replace : false;
            const force = "force" in params ? params.force : false;

            if (replace && widgets.includes(replace)) {
                trackClosed(replace);
                trackOpened(widgetKey);
                dispatch(
                    explorerActions.replaceWidgetSlot({
                        replace: replace,
                        key: widgetKey,
                    }),
                );
            } else if (force) {
                if (getTakenWidgetSlotCount(widgets, maximized, maximizedHorizontal) >= widgetLayout.widgets) {
                    const evicted = widgets[widgets.length - 1];
                    trackClosed(evicted);
                }
                trackOpened(widgetKey);
                dispatch(explorerActions.forceOpenWidget(widgetKey));
                showFollowPathSnackbar();
            } else if (canAddWidget) {
                trackOpened(widgetKey);
                dispatch(explorerActions.addWidgetSlot(widgetKey));
                if (widgetSlot.open) {
                    dispatch(
                        explorerActions.setWidgetSlot({
                            open: false,
                            group: undefined,
                        }),
                    );
                }
                showFollowPathSnackbar();
            } else {
                let msg: string;
                if (widgetLayout.widgets > 1 && widgets.length === 1) {
                    msg = t("closeOrResizeTheWidgetToAddANewOne");
                } else if (widgetLayout.widgets === 1) {
                    msg = t("closeTheWidgetToAddANewOne");
                } else if (widgetLayout.widgets !== widgets.length) {
                    msg = t("closeOrResizeOneOfTheWidgetsToAddANewOne");
                } else {
                    msg = t("closeOneOfTheWidgetsToAddANewOne");
                }
                dispatch(
                    explorerActions.setSnackbarMessage({
                        msg,
                        closeAfter: 5000,
                    }),
                );
            }
        },
        [dispatch, store, t],
    );
}

function trackOpened(key: WidgetKey) {
    mixpanel?.track("Opened Widget", { "Widget Key": key });
}

function trackClosed(key: WidgetKey) {
    mixpanel?.track("Closed Widget", { "Widget Key": key });
}
